import { buildMetricSeries } from '@components/metrics-panel-hz/utils/metricSeriesOption';
import { getMetricMetaByKey } from '@src/utils/metrics';
import { usageMetricList } from '../atoms/index';
import { chartOption, utcMoment } from '../utils';
import { NO_PLAN_LIMIT_METRIC } from '@components/usage-dashboard/constant';
import { humanize } from '@utils';
import { cloneDeep } from 'lodash';
import { usageIncidentMarkArea, withUsageIncidentTooltip } from '../usageIncident';

export const transformDataForChart = ({ metrics, data, totals }) => {
  const result = {};
  const allMetrics = getUsageMetricsMeta();
  metrics.forEach((m) => (result[m] = []));

  data.forEach((dataPoint) => {
    metrics.forEach((metric) => {
      let value = dataPoint.results[metric];
      const unit = allMetrics[metric].unit;
      if (unit === 'mins') {
        value = value / 60;
      }
      result[metric].push([dataPoint.timestamp, value]);
    });
  });
  // convert from mins to hours
  metrics.forEach((metric) => {
    const unit = allMetrics[metric].unit;
    if (!totals[metric]) totals[metric] = null;
    if (unit === 'mins' && totals[metric]) {
      totals[metric] = totals[metric] / 60;
    }
  });
  return { data: result, totals };
};

type TMarkLineSegment = [{ coord: [string, number] }, { coord: [string, number] }];

export const transformPlanLimitDataToMarkLineData = ({
  data,
  totals,
}: {
  data: {
    timestamp: string;
    results: { [key: string]: number };
  }[];
  totals: { [key: string]: number };
}) => {
  if (data?.length === 0) return { data: {}, totals };

  // Chronological order
  const sortedData = data
    .slice()
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const metrics = new Set<string>();
  sortedData.forEach((item) => Object.keys(item.results).forEach((k) => metrics.add(k)));
  const result: Record<string, TMarkLineSegment[]> = {};

  metrics.forEach((metric) => {
    const pairs: [string, number][] = [];

    sortedData.forEach((item) => {
      const currentTime = item.timestamp;
      const currentValue = item.results[metric] ?? 0;
      pairs.push([currentTime, currentValue]);
    });

    // Generated mark line segment
    const segments: TMarkLineSegment[] = [];
    pairs.forEach((item, index) => {
      if (pairs.length === 1) {
        segments.push([{ coord: item }, { coord: item }]);
      } else {
        // Skip last
        if (index === pairs.length - 1) return;

        const [t1, v1] = item;
        const [t2, v2] = pairs[index + 1];

        // Determine whether the limit value has changed
        if (v1 === v2) {
          // Horizontal line
          segments.push([{ coord: [t1, v1] }, { coord: [t2, v2] }]);
        } else {
          // Horizontal line
          segments.push([{ coord: [t1, v1] }, { coord: [t2, v1] }]);
          // Vertical line
          segments.push([{ coord: [t2, v1] }, { coord: [t2, v2] }]);
        }
      }
    });

    result[metric] = segments;
  });

  return { data: result, totals };
};

function filterMarklineDataBySeriesTimeRange(series, markline) {
  if (!Array.isArray(series?.data) || series?.data?.length === 0 || !Array.isArray(markline?.data))
    return markline;

  const seriesStart = new Date(series.data[0][0]).getTime();
  const seriesEnd = new Date(series.data[series.data.length - 1][0]).getTime();
  const dataInTimeRange: Array<Array<{ coord: [string, number] }>> = [];

  markline.data.forEach((line) => {
    const [p1, p2] = line;
    const [t1, v1] = p1.coord;
    const [t2, v2] = p2.coord;
    const t1Time = new Date(t1).getTime();
    const t2Time = new Date(t2).getTime();

    // Processing vertical line segments (same time)
    if (t1Time === t2Time) {
      if (t1Time >= seriesStart && t1Time <= seriesEnd) {
        dataInTimeRange.push(line);
      }
      // Processing horizontal line segments (same value)
    } else {
      if (t2Time >= seriesStart && t2Time <= seriesEnd) {
        dataInTimeRange.push(line);
      }
    }
  });

  return {
    ...markline,
    data: dataInTimeRange,
  };
}

// The plan limit may not be above the YAxis, so it is necessary to determine the maximum value of the YAxis to expand the YAxis.
function getYAxisMaxOption({ series, markline }) {
  if (
    !Array.isArray(series?.data) ||
    !Array.isArray(markline?.data) ||
    markline?.data?.length === 0
  )
    return {};
  // Get the maximum value in series.data using map and Math.max
  const seriesMax = Math.max(...series.data.map((item) => item[1]));

  // Use flat to flatten markline.data, then extract all y values to calculate the maximum value
  const marklineMax = Math.max(...markline.data.flat().map((point) => point.coord[1]));

  // Compare marklineMax with seriesMax and return the corresponding yAxis configuration object
  return marklineMax > seriesMax ? { max: marklineMax } : {};
}

export function buildSeriesData({ instance, data, allMetrics, metric, markLine, interval }) {
  allMetrics[metric].unit = '';
  let option = instance.getOption();
  if (option) {
    if (option._viewType !== 'timeseries') {
      const metricChartOption = chartOption(metric.metricName);
      instance.setOption(metricChartOption, { notMerge: true });
      option = instance.getOption();
    }
    const markLineInSeriesTimeRange = filterMarklineDataBySeriesTimeRange({ data }, markLine);
    const { series, xAxis, yAxis, tooltip } = formatSeries({
      option,
      data,
      metric,
      params: { filterColor: '#0f93f5' },
      allMetrics,
      markLine: markLineInSeriesTimeRange,
      interval,
    });

    const yAxisMaxOption = getYAxisMaxOption({
      series: { data },
      markline: markLineInSeriesTimeRange,
    });

    // markline time is returned by day, so you need to make sure that each time point is found on the xAxis.
    if (xAxis?.[0]) {
      xAxis[0].type = 'time';
    }

    instance.setOption({
      series,
      xAxis,
      yAxis: [{ ...yAxis?.[0], ...yAxisMaxOption }],
      tooltip,
    });
  }
}

export function formatSeries({
  option,
  data = [],
  metric,
  params,
  allMetrics,
  markLine,
  interval,
}) {
  // add or update metric filter series
  const { metricChartData, xAxis, yAxis, tooltip } = buildMetricSeries({
    option,
    data,
    metric,
    params,
    allMetrics,
    moment: utcMoment,
  });

  tooltip[0].formatter = withUsageIncidentTooltip(tooltip[0].formatter, interval);

  return {
    series: [{ ...metricChartData, markLine, markArea: usageIncidentMarkArea(interval) }],
    xAxis,
    yAxis,
    tooltip,
  };
}

export const getUsageMetricsMeta = (data = usageMetricList) => {
  Object.keys(data).forEach((key) => {
    const metricMeta = getMetricMetaByKey({
      metricKey: key,
      context: data[key].product,
    });
    data[key] = { ...metricMeta, ...data[key] };
  });

  return data;
};

function mergeSameLimitSegments(segments: { coord: [string, number]; planLimitValue: number }[][]) {
  // merge is performed only when greater than or equal to 2
  if (segments?.length < 2) return segments;

  const copiedSegments = cloneDeep(segments);

  const result: { coord: [string, number]; planLimitValue: number }[][] = [];

  for (let i = 0; i < copiedSegments.length; i++) {
    if (i === 0) {
      result.push(copiedSegments[0]);
    } else {
      const [startPoint, endPoint] = copiedSegments[i];
      const lastSegment = result[result.length - 1];
      const lastEndPoint = lastSegment[1];

      const isSameLimit =
        lastEndPoint?.coord?.[1] === startPoint?.coord?.[1] &&
        startPoint?.coord?.[1] === endPoint?.coord?.[1];

      // If there are two limit lines, the time is different, and the limit value is the same, the two lines are merged into one line
      if (lastEndPoint?.coord?.[0] === startPoint?.coord?.[0] && isSameLimit) {
        result[result.length - 1].pop();
        result[result.length - 1].push(endPoint);
      } else {
        // The limit value is different. It's a vertical line
        result.push(copiedSegments[i]);

        const isLast = i === copiedSegments.length - 1;
        if (!isLast) {
          // Vertical lines do not merge, so need to push another line in.
          i++;
          result.push(copiedSegments[i]);
        }
      }
    }
  }

  return result;
}

export const generateMarkLine = (data: any[], metric, chartData: any) => {
  if (!data || data?.length === 0) return;
  const usageMetricsMeta = getUsageMetricsMeta();

  // // Keep that starts and ends within a valid range
  if (chartData.length > 1) {
    const xAxisValidStartTime = new Date(chartData[0][0]).getTime();
    const xAxisValidEndTime = new Date(chartData[chartData.length - 1][0]).getTime();

    data = data.filter(([p1, p2]) => {
      const t1 = new Date(p1?.coord?.[0]).getTime();
      const t2 = new Date(p2?.coord?.[0]).getTime();

      return t1 >= xAxisValidStartTime && t2 <= xAxisValidEndTime;
    });
  }

  data.forEach((pair) => {
    pair.forEach((point) => {
      point.planLimitValue = point?.coord?.[1];
    });
  });

  const mergedLimitData = mergeSameLimitSegments(data);

  const markLineTooltip = {
    show: true,
    trigger: 'item',
    formatter: (params) => {
      const value = params?.data?.planLimitValue;
      return 'Limit: ' + humanize(value, usageMetricsMeta[metric].unit);
    },
  };

  // limit has only one point
  if (mergedLimitData?.length === 1) {
    const segment = mergedLimitData[0];
    const [start, end] = segment;
    // Determine if the coordinates are exactly the same
    if (JSON.stringify(start.coord) === JSON.stringify(end.coord)) {
      return {
        // The starting point is not marked and the end point is shown as a circle
        symbol: ['none', 'circle'],
        // Set point size
        symbolSize: 10,
        // Set point color
        itemStyle: {
          color: '#FF4D4F',
        },
        data: mergedLimitData,
        tooltip: markLineTooltip,
      };
    }
  }

  // limit more than 2 points can be drawn as a line
  return {
    symbol: 'none', // No arrowhead
    lineStyle: {
      type: 'dashed',
      color: '#FF4D4F',
      width: 3,
    },
    data: mergedLimitData,
    tooltip: markLineTooltip,
  };
};

// Determines not show plan limit metric
export const isNoPlanLimitMetric = (metricName: string) => {
  return NO_PLAN_LIMIT_METRIC.includes(metricName);
};

// Determines if the plan limit of a metric is equal to 0
export function isPlanLimitZero(planLimitData): boolean {
  // Check in 'data'
  if (Array.isArray(planLimitData?.data)) {
    for (const group of planLimitData.data) {
      for (const entry of group) {
        if (entry?.coord?.[1] !== 0) {
          return false;
        }
      }
    }
  }

  // Check in 'totals'
  return planLimitData?.totals == null || planLimitData?.totals === 0;
}
